
import React, { useEffect, useState } from 'react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import {
  Box,
  Button,
  Heading,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Icon,
  HStack,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Badge,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import {
  FiUpload,
  FiFile,
  FiCheckCircle,
  FiX,
  FiEye,
  FiDownload,
  FiClock,
  FiFileText,
  FiTag,
  FiPackage,
  FiUser,
  FiInbox,
} from 'react-icons/fi';
import { format } from 'date-fns';
import Select from 'react-select';
import { getScoreTypes, uploadStoreMaster, getUploadLog } from 'services/masterSheetsService';
import { exportAoaToExcel } from 'Utils/ExcelExport';
import { MASTER_SHEET_COLUMNS, MASTER_SHEET_SAMPLE_ROW } from './masterSheetTemplate';

const BRAND = '#d52b1f';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: 'white',
    borderColor: state.isFocused ? BRAND : '#CBD5E0',
    borderRadius: '8px',
    minHeight: '40px',
    boxShadow: state.isFocused ? `0 0 0 1px ${BRAND}` : 'none',
    '&:hover': { borderColor: '#A0AEC0' },
  }),
  singleValue: (base) => ({ ...base, color: '#2D3748' }),
  placeholder: (base) => ({ ...base, color: '#A0AEC0' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? BRAND : state.isFocused ? '#EBF8FF' : 'white',
    color: state.isSelected ? 'white' : '#2D3748',
  }),
};

const getCurrentMonthYear = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export default function MasterSheets() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const toast = useToast({
    position: 'top-right',
    isClosable: true,
  });
  const { isOpen: isSampleOpen, onOpen: onSampleOpen, onClose: onSampleClose } = useDisclosure();

  const [monthYear, setMonthYear] = useState(getCurrentMonthYear());
  const [year, month] = monthYear.split('-').map(Number);

  const [scoreTypes, setScoreTypes] = useState([]);
  const [scoreTypesLoading, setScoreTypesLoading] = useState(true);
  const [selectedScoreType, setSelectedScoreType] = useState(null);

  const scoreTypeOptions = scoreTypes.map((scoreType) => ({
    value: scoreType.scoretypeId,
    label: scoreType.tradeType,
  }));

  const [recentUploads, setRecentUploads] = useState([]);
  const [recentUploadsLoading, setRecentUploadsLoading] = useState(true);
  const [recentUploadsError, setRecentUploadsError] = useState('');

  const fetchRecentUploads = async (m, y, scoretypeId) => {
    if (!scoretypeId) return;
    setRecentUploadsLoading(true);
    setRecentUploadsError('');
    try {
      const response = await getUploadLog(m, y, scoretypeId, 3);
      const records = Array.isArray(response.data) ? response.data : response.data?.data || [];
      const sorted = [...records].sort(
        (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
      );
      setRecentUploads(sorted.slice(0, 3));
    } catch (error) {
      setRecentUploadsError('Failed to load recent uploads');
    } finally {
      setRecentUploadsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedScoreType?.value) {
      fetchRecentUploads(month, year, selectedScoreType.value);
    }
  }, [selectedScoreType, month, year]);

  useEffect(() => {
    let isMounted = true;

    const loadScoreTypes = async () => {
      try {
        const response = await getScoreTypes();
        if (isMounted) {
          const activeScoreTypes = (response.data || []).filter((item) => item.isActive);
          setScoreTypes(activeScoreTypes);

          const redScoreType = activeScoreTypes.find((item) => item.tradeType=== 'GT');
          if (redScoreType) {
            setSelectedScoreType({
              value: redScoreType.scoretypeId,
              label: redScoreType.tradeType,
            });
          }
        }
      } catch (error) {
        if (isMounted) {
          toast({
            title: 'Error',
            description: 'Failed to load score types',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        if (isMounted) setScoreTypesLoading(false);
      }
    };

    loadScoreTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  // File validation
  const validateFile = (file) => {
    const allowedExtensions = ['.xlsx', '.xls'];
    const maxSize = 10 * 1024 * 1024;

    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      return 'Please select a valid Excel file (.xlsx or .xls)';
    }

    if (file.size === 0) {
      return 'Selected file is empty';
    }

    if (file.size > maxSize) {
      return 'File size should not exceed 10MB';
    }

    return null;
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const validationError = validateFile(file);

      if (validationError) {
        setErrorMessage(validationError);
        setSelectedFile(null);
        setUploadStatus('error');
        return;
      }

      setSelectedFile(file);
      setErrorMessage('');
      setUploadStatus(null);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setMonthYear(getCurrentMonthYear());
    const redScoreType = scoreTypes.find((item) => item.tradeType === 'RED');
    setSelectedScoreType(
      redScoreType ? { value: redScoreType.scoretypeId, label: redScoreType.tradeType} : null
    );
    const fileInput = document.getElementById('file-input');
    if (fileInput) fileInput.value = '';
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select an Excel file to upload',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedScoreType) {
      toast({
        title: 'No score type selected',
        description: 'Please select a score type',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setUploadStatus(null);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await uploadStoreMaster(
        selectedFile,
        month,
        year,
        selectedScoreType.value
      );

      const message = response.data || 'Upload successful';
      setUploadStatus('success');
      setSuccessMessage(message);
      toast({
        title: 'Upload Successful',
        description: message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      fetchRecentUploads(month, year, selectedScoreType.value);
      resetForm();
    } catch (error) {
      setUploadStatus('error');

      if (error.response) {
        const apiMessage =
          typeof error.response.data === 'string' && error.response.data
            ? error.response.data
            : 'Upload failed';
        setErrorMessage(apiMessage);
        toast({
          title: 'Upload Failed',
          description: apiMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        const networkMessage = 'Network error: could not reach the server. Please check your connection and try again.';
        setErrorMessage(networkMessage);
        toast({
          title: 'Network Error',
          description: networkMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setUploadStatus(null);
    setErrorMessage('');
    const fileInput = document.getElementById('file-input');
    if (fileInput) fileInput.value = '';
  };

  const handleDownloadTemplate = () => {
    exportAoaToExcel({
      headers: MASTER_SHEET_COLUMNS,
      rows: [MASTER_SHEET_SAMPLE_ROW],
      sheetName: 'Master Sheet Template',
      fileName: 'MasterSheet_Template',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUploadedAt = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    return format(date, "MMM d, yyyy, hh:mm a");
  };

  const formatStoreCount = (count) => {
    if (count === null || count === undefined || count === '') return '—';
    const num = Number(count);
    return Number.isNaN(num) ? '—' : num.toLocaleString();
  };

  return (
    <Box py={8}>
      <Box
        pl={0}
        pr={8}
        mb={6}
        mt={10}
        display="flex"
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        gap={4}
      >
        <Box>
          <Heading size="lg" mb={2}>
            Masters Sheet Upload
          </Heading>
          <Text color="gray.600">
            Upload monthly  Master allocation sheet to update store master data
          </Text>
        </Box>

        <HStack spacing={4} align="flex-end" flexWrap="wrap">
          <Box minW="180px">
            <FormLabel fontSize="md" color="gray.800" fontWeight="bold" mb={1}>
              Trade Type
            </FormLabel>
            <Select
              placeholder={scoreTypesLoading ? 'Loading...' : 'Select score type'}
              options={scoreTypeOptions}
              value={selectedScoreType}
              onChange={(option) => setSelectedScoreType(option)}
              isDisabled={scoreTypesLoading || isLoading}
              isLoading={scoreTypesLoading}
              isClearable={false}
              styles={selectStyles}
            />
          </Box>

          <Box minW="210px">
            <FormLabel fontSize="md" color="gray.800" fontWeight="bold" mb={1}>
              Month &amp; Year
            </FormLabel>
            <Box
              as="input"
              type="month"
              value={monthYear}
              onChange={(e) => e.target.value && setMonthYear(e.target.value)}
              disabled={isLoading}
              sx={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'gray.300',
                bg: 'white',
                fontSize: '16px',
                cursor: 'pointer',
                '&:focus': {
                  outline: 'none',
                  borderColor: BRAND,
                  boxShadow: `0 0 0 3px rgba(213,43,31,0.3)`,
                },
              }}
            />
          </Box>
        </HStack>
      </Box>

      <Divider mb={6} />
      <Box display="flex" justifyContent="center" px={8}>
        <Card width="100%" maxW="600px">
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Box p={4} borderWidth={1} borderRadius="md" borderColor="gray.200" bg="gray.50">
                <Text fontSize="sm" color="gray.700" mb={3}>
                 Check the required columns before uploading.
                </Text>
                <HStack spacing={3} flexWrap="wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor={BRAND}
                    color={BRAND}
                    _hover={{ bg: 'rgba(213,43,31,0.08)' }}
                    _active={{ bg: 'rgba(213,43,31,0.15)' }}
                    leftIcon={<Icon as={FiEye} />}
                    onClick={onSampleOpen}
                  >
                    View Sample Format
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    leftIcon={<Icon as={FiDownload} />}
                    onClick={handleDownloadTemplate}
                  >
                    Download Sample Template
                  </Button>
                </HStack>
              </Box>

              <FormControl isInvalid={uploadStatus === 'error' && !!errorMessage}>
                <FormLabel>Select Excel File</FormLabel>
                <Input
                  id="file-input"
                  type="file"
                  accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={handleFileChange}
                  disabled={isLoading}
                  display="none"
                />
                <Button
                  as="label"
                  htmlFor="file-input"
                  leftIcon={<Icon as={FiFile} />}
                  variant="outline"
                  borderColor={BRAND}
                  color={BRAND}
                  _hover={{ bg: 'rgba(213,43,31,0.08)' }}
                  _active={{ bg: 'rgba(213,43,31,0.15)' }}
                  width="100%"
                  cursor="pointer"
                  isDisabled={isLoading}
                >
                  Choose Excel File
                </Button>
                <FormErrorMessage>{errorMessage}</FormErrorMessage>
              </FormControl>
              {selectedFile && (
                <Box
                  p={4}
                  borderWidth={1}
                  borderRadius="md"
                  borderColor="blue.200"
                  bg="blue.50"
                >
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <Icon as={FiFile} color="blue.500" boxSize={5} />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" fontSize="sm">
                          {selectedFile.name}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {formatFileSize(selectedFile.size)}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      leftIcon={<Icon as={FiX} />}
                      onClick={handleClear}
                      isDisabled={isLoading}
                    >
                      Remove
                    </Button>
                  </HStack>
                </Box>
              )}
              {isLoading && (
                <Box>
                  <Text mb={2} fontSize="sm" color="gray.600">
                    Uploading and processing...
                  </Text>
                  <Progress size="sm" isIndeterminate colorScheme="blue" />
                </Box>
              )}

              {uploadStatus === 'success' && (
                <Alert status="success" borderRadius="md">
                  <AlertIcon as={FiCheckCircle} />
                  <Box>
                    <AlertTitle>Upload Successful!</AlertTitle>
                    <AlertDescription>{successMessage}</AlertDescription>
                  </Box>
                </Alert>
              )}

              {uploadStatus === 'error' && errorMessage && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Upload Failed</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Box>
                </Alert>
              )}

              {/* Upload Button */}
              <Button
                leftIcon={<Icon as={FiUpload} />}
                bg={BRAND}
                color="white"
                _hover={{ bg: '#b8241a' }}
                _active={{ bg: '#a02017' }}
                size="lg"
                onClick={handleUpload}
                isLoading={isLoading}
                loadingText="Uploading..."
                isDisabled={!selectedFile || !selectedScoreType || isLoading}
                width="100%"
              >
                Upload
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Box>

      <Box px={8} mt={8}>
        <HStack spacing={2} mb={3}>
          <Icon as={FiClock} color={BRAND} boxSize={5} />
          <Heading size="sm" color="gray.800">
            Recent Uploads
          </Heading>
        </HStack>

        {recentUploadsLoading ? (
          <Flex justify="center" align="center" height="110px">
            <Spinner size="md" color={BRAND} />
          </Flex>
        ) : recentUploadsError ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription fontSize="sm">{recentUploadsError}</AlertDescription>
          </Alert>
        ) : recentUploads.length === 0 ? (
          <Flex
            align="center"
            justify="center"
            direction="column"
            height="90px"
            borderWidth={1}
            borderStyle="dashed"
            borderColor="gray.200"
            borderRadius="md"
            color="gray.400"
          >
            <Icon as={FiInbox} boxSize={5} mb={1} />
            <Text fontSize="sm">No recent uploads found</Text>
          </Flex>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {recentUploads.map((upload, index) => (
              <Card
                key={`${upload.fileName}-${upload.uploadedAt}-${index}`}
                p={0}
                overflow="hidden"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
                transition="all 0.2s"
                _hover={{ boxShadow: 'md', borderColor: BRAND }}
              >
                <Box h="4px" bg={index === 0 ? BRAND : 'gray.200'} />
                <CardBody p={4}>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between" align="start">
                      <HStack spacing={2} align="start" flex="1" minW={0}>
                        <Icon as={FiFileText} color={BRAND} boxSize={4} mt="2px" flexShrink={0} />
                        <Text fontWeight="semibold" fontSize="sm" noOfLines={1} title={upload.fileName}>
                          {upload.fileName || '—'}
                        </Text>
                      </HStack>
                      {index === 0 && (
                        <Badge colorScheme="red" fontSize="9px" borderRadius="full" px={2} flexShrink={0}>
                          Latest
                        </Badge>
                      )}
                    </HStack>

                    <HStack
                      spacing={2}
                      bg={index === 0 ? 'red.50' : 'gray.50'}
                      borderRadius="md"
                      px={3}
                      py={2}
                    >
                      <Icon as={FiClock} color={index === 0 ? BRAND : 'gray.500'} boxSize={4} />
                      <Text fontSize="sm" fontWeight="bold" color={index === 0 ? BRAND : 'gray.700'}>
                        {formatUploadedAt(upload.uploadedAt)}
                      </Text>
                    </HStack>

                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <HStack spacing={1.5} color="gray.500">
                          <Icon as={FiTag} boxSize={3.5} />
                          <Text fontSize="xs">Trade Type</Text>
                        </HStack>
                        <Badge colorScheme="blue" fontSize="xs">
                          {upload.tradeType || '—'}
                        </Badge>
                      </HStack>

                      <HStack justify="space-between">
                        <HStack spacing={1.5} color="gray.500">
                          <Icon as={FiPackage} boxSize={3.5} />
                          <Text fontSize="xs">Store Count</Text>
                        </HStack>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatStoreCount(upload.storeCount)}
                        </Text>
                      </HStack>

                      <HStack justify="space-between">
                        <HStack spacing={1.5} color="gray.500">
                          <Icon as={FiUser} boxSize={3.5} />
                          <Text fontSize="xs">Uploaded By</Text>
                        </HStack>
                        <Text fontSize="sm" fontWeight="medium">
                          {upload.username || 'N/A'}
                        </Text>
                      </HStack>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Box>

      <Modal isOpen={isSampleOpen} onClose={onSampleClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Sample Master Sheet Format</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Your Excel file must contain the following {MASTER_SHEET_COLUMNS.length} columns, in this
              order, with headers matching exactly. An example row is shown below for reference.
            </Text>
            <TableContainer borderWidth={1} borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead bg="gray.100">
                  <Tr>
                    {MASTER_SHEET_COLUMNS.map((col) => (
                      <Th key={col} whiteSpace="nowrap" fontSize="xs">
                        {col}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    {MASTER_SHEET_SAMPLE_ROW.map((value, index) => (
                      <Td key={MASTER_SHEET_COLUMNS[index]} whiteSpace="nowrap" fontSize="xs">
                        {value}
                      </Td>
                    ))}
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              colorScheme="gray"
              leftIcon={<Icon as={FiDownload} />}
              mr={3}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
            <Button colorScheme="blue" onClick={onSampleClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}